// src/features/equipment/components/maintenance/OpenRepairBanner.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertTitle, Button } from '@mui/material';
import { Build as BuildIcon } from '@mui/icons-material';

import { useFormatDateTime } from './MaintenanceStatusChip';

/**
 * One-line summary of what's wrong with a device, shown above the tabs so it's
 * seen without opening the repairs tab. A running repair covers the device's
 * open reports, so only the repair is shown then.
 */
export default function OpenRepairBanner({ equipment, openCases, onOpenCase, onShowAll }) {
  const { t } = useTranslation(['equipment']);
  const formatDateTime = useFormatDateTime();
  const repair = openCases.find(c => c.status === 'in_progress');
  const reports = openCases.filter(c => c.status === 'reported');

  if (repair) {
    return (
      <Alert
        severity="info"
        icon={<BuildIcon />}
        sx={{ mb: 2 }}
        action={(
          <Button color="inherit" size="small" onClick={() => onOpenCase(repair.maintenance_id)}>
            {t('equipment:maintenance.actions.open_case')}
          </Button>
        )}
      >
        <AlertTitle>{t('equipment:maintenance.banner.repair', { title: repair.title })}</AlertTitle>
        {t('equipment:maintenance.pulled_by_at', { name: repair.pulled_by?.name ?? '–', date: formatDateTime(repair.pulled_at) })}
        {repair.service_provider && ` · ${repair.service_provider}`}
      </Alert>
    );
  }

  if (!reports.length) return null;
  const single = reports.length === 1 ? reports[0] : null;
  const outOfService = equipment.status === 'out_of_service';

  return (
    <Alert
      severity={outOfService ? 'error' : 'warning'}
      sx={{ mb: 2 }}
      action={(
        <Button
          color="inherit"
          size="small"
          onClick={() => (single ? onOpenCase(single.maintenance_id) : onShowAll())}
        >
          {single ? t('equipment:maintenance.actions.open_case') : t('equipment:maintenance.actions.show_all')}
        </Button>
      )}
    >
      <AlertTitle>
        {single
          ? t('equipment:maintenance.banner.report', { title: single.title })
          : t('equipment:maintenance.banner.reports', { count: reports.length })}
      </AlertTitle>
      {single
        ? t('equipment:maintenance.reported_by_at', { name: single.reported_by?.name ?? '–', date: formatDateTime(single.reported_at) })
        : reports.map(c => c.title).join(', ')}
      {outOfService && ` · ${t('equipment:maintenance.banner.not_usable')}`}
    </Alert>
  );
}
